import { useEffect, useState } from "react";
import VoucherConditionModal from "./VoucherConditionModal";
import { deleteSavedVourcher, fetchMyVourcherList } from "api/Vourcher";
import { MyVourcherItem } from "api/VourcherType";
import { toast } from "react-toastify";

function getRemainingTimeText(expiresAt: string | null, now: number): string {
    if (!expiresAt) return "Không giới hạn";
    const target = new Date(expiresAt).getTime();
    if (Number.isNaN(target)) return "Không xác định";

    const diffMs = target - now;
    if (diffMs <= 0) return "Đã hết hạn";

    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${days} ngày, ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function getVoucherTitle(item: MyVourcherItem): string {
    const valueNum = Number(item.voucher.value || 0);
    if (item.voucher.type === 'percent') return `GIẢM ${valueNum}%`;
    if (item.voucher.type === 'ship') return 'GIẢM PHÍ SHIP';
    if (item.voucher.type === 'gift') return 'QUÀ TẶNG';
    return `GIẢM ${new Intl.NumberFormat('vi-VN').format(valueNum)}đ`;
}

function getVoucherDesc(item: MyVourcherItem): string {
    const firstRule = item.voucher.rules?.find(rule => !!rule.rule_description);
    if (firstRule?.rule_description) return firstRule.rule_description;
    return item.voucher.name || item.voucher.code;
}

export default function MyVoucherSection() {
    const [openVoucher, setOpenVoucher] = useState(false);
    const [activeVoucherId, setActiveVoucherId] = useState<number | null>(null);
    const [vouchers, setVouchers] = useState<MyVourcherItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [now, setNow] = useState(Date.now());

    const loadMyVouchers = async () => {
        setLoading(true);
        const res = await fetchMyVourcherList();
        if (res.status && res.data?.items) {
            setVouchers(res.data.items);
        } else {
            setVouchers([]);
            if (res.msg) {
                toast.error(res.msg, {
                    position: "bottom-right",
                });
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        loadMyVouchers();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleUnsaveVoucher = async (id: number, status: string) => {
        if (status !== 'AVAILABLE') {
            toast.warning('Chỉ có thể bỏ lưu voucher ở trạng thái AVAILABLE.', {
                position: "bottom-right",
            });
            return;
        }

        setRemovingId(id);
        const res = await deleteSavedVourcher(id);
        setRemovingId(null);

        if (res.status) {
            toast.success(res.data?.message || 'Đã bỏ lưu voucher thành công.', {
                position: "bottom-right",
            });
            setVouchers(prev => prev.filter(item => item.voucher.id !== id));
            return;
        }

        toast.error(res.error?.message || res.msg || 'Không thể bỏ lưu voucher.', {
            position: "bottom-right",
        });
    };

    return (
        <div className="MuiContainer-root styles_container__3w0uC MuiContainer-maxWidthLg">
            <div id="sectionMyVoucher" style={{ scrollMarginTop: "115px" }}>
                <div className="MuiBox-root css-yd8sa2">
                    {/* ===== GRID LIST ===== */}
                    <div className="MuiGrid-root MuiGrid-container">
                        {loading && (
                            <div className="MuiGrid-root w-50-desktop MuiGrid-item MuiGrid-grid-xs-12" style={{ padding: 16 }}>
                                Đang tải danh sách voucher...
                            </div>
                        )}

                        {!loading && vouchers.length === 0 && (
                            <div className="MuiGrid-root w-50-desktop MuiGrid-item MuiGrid-grid-xs-12" style={{ padding: 16 }}>
                                Chưa có voucher nào đã lưu.
                            </div>
                        )}

                        {!loading && vouchers.map((item) => (
                            <div
                                key={item.customer_voucher_id}
                                className="MuiGrid-root w-50-desktop MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-"
                                style={{ position: "relative", borderRadius: 12, overflow: "hidden" }}
                            >
                                <div
                                    className="styles_voucherRightMissingPart__ghZZb styles_voucherRightMissingPartWithMessage__O2NeA"
                                    style={{ backgroundColor: "rgb(244, 247, 252)" }}
                                />
                                <div
                                    className="styles_voucherLeftMissingPart__Dd9yV styles_voucherLeftMissingPartWithMessage__1TuQu"
                                    style={{ backgroundColor: "rgb(244, 247, 252)" }}
                                />

                                <div
                                    className="MuiBox-root css-0"
                                    style={{
                                        position: "relative",
                                        borderRadius: 12,
                                        border: "1px solid rgb(235, 235, 235)",
                                    }}
                                >
                                    <div className="MuiGrid-root" style={{ position: "relative", overflow: "hidden" }}>
                                        <div className="MuiGrid-root styles_voucherItem_wrapper__6dbHT">
                                            <div className="MuiGrid-root styles_voucherContainer_upper__P4pdw MuiGrid-container">
                                                <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-9 MuiGrid-grid-md-9">
                                                    <p className="MuiTypography-root styles_voucherContent_title__W0Q3Z MuiTypography-body2 MuiTypography-noWrap">
                                                        {getVoucherTitle(item)}
                                                    </p>
                                                    <p className="MuiTypography-root MuiTypography-body2">
                                                        <div className="styles_voucherContent_desc__ANOqH styles_content_2Line__BrNP1">
                                                            {getVoucherDesc(item)}
                                                        </div>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="styles_voucherDivider__7rclz" />

                                            <div className="MuiGrid-root styles_voucherContainer_downer__DSU6c">
                                                <div className="MuiGrid-root MuiGrid-container">
                                                    <div className="MuiGrid-root styles_expiredDateBarWrapper__ffAl6 MuiGrid-item MuiGrid-grid-sm-4 MuiGrid-grid-md-5">
                                                        <p
                                                            className="MuiTypography-root MuiTypography-body2"
                                                            style={{
                                                                lineHeight: "14px",
                                                                color: "rgb(0, 0, 0)",
                                                                fontFamily: "ggsm",
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            <span style={{ color: "rgb(212, 50, 59)" }}>
                                                                {item.expires_at ? `Còn ${getRemainingTimeText(item.expires_at, now)}` : 'Không giới hạn'}
                                                            </span>
                                                        </p>
                                                    </div>

                                                    <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-md-7">
                                                        <div
                                                            className="MuiGrid-root"
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "flex-end",
                                                                gap: 8,
                                                            }}
                                                        >
                                                            <button
                                                                className="MuiButtonBase-root MuiButton-root MuiButton-text"
                                                                tabIndex={0}
                                                                type="button"
                                                                style={{
                                                                    color: "rgb(14, 25, 131)",
                                                                    padding: "6px 8px",
                                                                    textTransform: "unset",
                                                                }}
                                                                onClick={() => {
                                                                    setActiveVoucherId(item.voucher.id);
                                                                    setOpenVoucher(true);
                                                                }}
                                                            >
                                                                <span className="MuiButton-label">
                                                                    <svg
                                                                        className="MuiSvgIcon-root"
                                                                        focusable="false"
                                                                        viewBox="0 0 24 24"
                                                                        aria-hidden="true"
                                                                        style={{ width: 20, height: 20 }}
                                                                    >
                                                                        <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                                                                    </svg>
                                                                    <p className="MuiTypography-root MuiTypography-body2" style={{ paddingLeft: 8, fontSize: 12, lineHeight: "18px" }}>
                                                                        Điều kiện
                                                                    </p>
                                                                </span>
                                                                <span className="MuiTouchRipple-root" />
                                                            </button>

                                                            {item.status === 'USED' || item.status === 'RESERVED' ? (
                                                                <button
                                                                    className="MuiButtonBase-root MuiButton-root MuiButton-text styles_voucherCondition_btnUsing__x1T_J styles_voucherCondition_btn__UY8KH"
                                                                    tabIndex={0}
                                                                    type="button"
                                                                    disabled
                                                                >
                                                                    <span className="MuiButton-label">
                                                                        <span>{item.status === 'USED' ? 'Đã sử dụng' : 'Đang giữ chỗ'}</span>
                                                                    </span>
                                                                    <span className="MuiTouchRipple-root" />
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="MuiButtonBase-root MuiButton-root MuiButton-text styles_voucherCondition_btnBuyMore__l47_i styles_voucherCondition_btn__UY8KH"
                                                                    type="button"
                                                                    disabled={removingId === item.voucher.id}
                                                                    onClick={() => handleUnsaveVoucher(item.voucher.id, item.status)}
                                                                >
                                                                    <span className="MuiButton-label"><span>{removingId === item.voucher.id ? 'Đang xử lý...' : 'Bỏ lưu'}</span></span><span className="MuiTouchRipple-root"></span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {(item.status === 'EXPIRED') && (
                                        <div className="MuiGrid-root styles_errorMessage_buyMoreContainer__a0rSG">
                                            <p className="MuiTypography-root MuiTypography-body2">
                                                <span
                                                    style={{
                                                        boxSizing: "border-box",
                                                        display: "inline-block",
                                                        overflow: "hidden",
                                                        width: "100%",
                                                        height: "initial",
                                                        background: "none",
                                                        opacity: 1,
                                                        border: 0,
                                                        margin: 0,
                                                        padding: 0,
                                                        position: "relative",
                                                        maxWidth: "16px",
                                                    }}
                                                >
                                                    <img className="!w-4 !h-4" alt="error icon" data-origin-url="https://cdn-web-next.thuocsi.vn/images/promoCode/error_code_icon.svg" src="https://cdn-web-next.thuocsi.vn/images/promoCode/error_code_icon.svg" decoding="async" data-nimg="intrinsic" />
                                                </span>

                                                Voucher đã hết hạn.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <VoucherConditionModal
                open={openVoucher}
                voucherId={activeVoucherId}
                onClose={() => {
                    setOpenVoucher(false);
                    setActiveVoucherId(null);
                }}
            />
        </div>
    );
}
